import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { api, ApiError, type LoginResponse } from "../api.js";
import { API_BASE_URL, TURNSTILE_SITE_KEY } from "../config.js";
import {
  clearSession,
  setAccessToken,
  setActiveOrgSlug,
  setUser,
} from "../auth.js";
import { Button } from "@/components/ui/button";
import { Turnstile } from "@/components/app/Turnstile";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { usePageSeo } from "@/lib/usePageTitle";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

// Convert an organisation name into a valid workspace slug:
// lowercase, strip accents, replace runs of non-alphanumerics with
// a single hyphen, trim edge hyphens, clip to 40 chars. Used to
// auto-populate the slug input from the Organisation name field
// during signup — the user can still override by typing into the
// slug input directly.
function slugifyOrgName(name: string): string {
  return (name ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
}

function passwordStrength(p: string): { label: string; tone: "warn" | "ok" | "strong" | "none" } {
  if (p.length === 0) return { label: "", tone: "none" };
  if (p.length < 10) return { label: `At least 10 characters (${p.length}/10)`, tone: "warn" };
  if (p.length < 14) return { label: "OK, 10+ characters", tone: "ok" };
  return { label: "Strong, 14+ characters", tone: "strong" };
}

export function Login() {
  const nav = useNavigate();
  const location = useLocation();
  // Preserve deep-link after login — if a user was redirected here
  // by RequireAuth from `/platform/questions/abc`, we should return
  // them there on success, not dump them at /dashboard. Supports
  // both router-state `from` and `?from=` query param for the case
  // where the browser restored the URL fresh.
  const redirectTo = (() => {
    const state = location.state as { from?: { pathname: string; search?: string } } | null;
    if (state?.from?.pathname && state.from.pathname !== "/login") {
      return state.from.pathname + (state.from.search ?? "");
    }
    const qs = new URLSearchParams(location.search);
    const from = qs.get("from");
    if (from && from.startsWith("/") && !from.startsWith("/login")) return from;
    return "/dashboard";
  })();
  const [mode, setMode] = useState<"login" | "register">(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get("mode") === "register" ? "register" : "login";
  });
  usePageSeo(
    mode === "login"
      ? {
          title: "Sign in",
          description:
            "Sign in to ExamBase, the UK Functional Skills English assessment platform for colleges and training providers.",
          path: "/login",
        }
      : {
          title: "Create workspace",
          description:
            "Create a free ExamBase workspace for your college or training centre. No learner limit during setup.",
          path: "/login?mode=register",
        },
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [orgSlug, setOrgSlug] = useState("");
  const [fullName, setFullName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<
    null | "credentials" | "rate_limit" | "suspended" | "not_confirmed" | "turnstile" | "other"
  >(null);
  const [busy, setBusy] = useState(false);
  // When a user belongs to >1 organisation, login can't bind the session to a
  // single tenant, so we show a picker instead of navigating in with an
  // unscoped session (which 403s every org call). The choice re-scopes via
  // switch-org.
  const [orgChoices, setOrgChoices] = useState<{
    user: LoginResponse["user"];
    memberships: LoginResponse["memberships"];
  } | null>(null);
  const [enteringSlug, setEnteringSlug] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugAvailability, setSlugAvailability] = useState<
    null | "checking" | "available" | "taken" | "invalid"
  >(null);
  // Track whether the user has hand-edited the slug so we stop
  // overwriting it from the org-name auto-derive. Reset if they
  // clear the field or re-enter register mode.
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  // `busy` is React state, so the disabled flag on the submit button only
  // applies after the next render. Two rapid clicks within one render tick
  // would both start an api.login()/api.register() call. submittingRef is
  // checked synchronously to short-circuit the second invocation.
  const submittingRef = useRef(false);
  const [ssoHint, setSsoHint] = useState<
    null | { organisation_slug: string; organisation_name: string | null }
  >(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileSettled, setTurnstileSettled] = useState(false);
  // Bumped after a failed submit so the Turnstile widget re-runs and mints a
  // fresh token. Its tokens are single-use, so without this a second attempt
  // (wrong password, then retry) POSTs a spent token and is hard-blocked.
  const [turnstileReset, setTurnstileReset] = useState(0);
  const onTurnstileToken = useCallback((t: string | null) => {
    setTurnstileToken(t);
    setTurnstileSettled(true);
  }, []);
  // A configured Turnstile must hold a real token before we POST (login /
  // register verify it server-side, no fail-open in production). Gate the
  // submit on holding a token — not on the widget merely having reported an
  // outcome (an expiry/error reports a null token). No site key configured
  // (dev/staging) → no gate.
  const turnstileMissing = !!TURNSTILE_SITE_KEY && !turnstileToken;
  const turnstileChecking = turnstileMissing && !turnstileSettled;

  // SSO callback hash consumer. The Worker redirects back with tokens in
  // `location.hash`; we pluck them into localStorage and send the user
  // straight to /dashboard, mirroring the normal login flow.
  useEffect(() => {
    if (!window.location.hash.startsWith("#")) return;
    const qs = new URLSearchParams(window.location.hash.slice(1));
    if (!qs.get("access_token")) return;
    try {
      setAccessToken(qs.get("access_token") ?? null);
      const slug = qs.get("organisation_slug");
      if (slug) setActiveOrgSlug(slug);
      setUser({
        id: qs.get("user_id") ?? "",
        email: qs.get("email") ?? "",
        full_name: qs.get("full_name") ?? "",
        role: qs.get("role") ?? undefined,
        active_organisation_id: qs.get("organisation_id"),
      });
      // Scrub the fragment so tokens don't linger in browser history.
      window.history.replaceState({}, "", window.location.pathname);
      nav(redirectTo, { replace: true });
    } catch {
      /* malformed fragment — fall through to the normal login form */
    }
  }, [nav, redirectTo]);

  // Focus email on mount so keyboard-only users can start typing
  // immediately. Skip if the browser autofilled it.
  useEffect(() => {
    if (!email && emailInputRef.current) emailInputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tenant-branded login. Fetches the tenant's public brand (logo + brand
  // colour) when the user types/selects a slug, renders a small card at
  // the top of the form. Unbranded tenants fall back to the default
  // ExamBase wordmark — so the login page always looks polished even if
  // the tenant hasn't set up branding.
  const [brand, setBrand] = useState<{
    slug: string;
    logo_r2_key: string | null;
    primary_hsl: string | null;
  } | null>(null);
  useEffect(() => {
    const slug = orgSlug.trim().toLowerCase();
    if (slug.length < 3 || !SLUG_PATTERN.test(slug)) {
      setBrand(null);
      return;
    }
    const timer = window.setTimeout(() => {
      void api
        .getPublicBrand(slug)
        .then((b) => {
          if (b.logo_r2_key || b.primary_hsl)
            setBrand({ slug, logo_r2_key: b.logo_r2_key, primary_hsl: b.primary_hsl });
          else setBrand(null);
        })
        .catch(() => setBrand(null));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [orgSlug]);

  // Auto-derive the slug from the org name as the user types it,
  // until they hand-edit the slug input — then we leave it alone.
  // Switching back to login mode clears the manual flag so the next
  // register attempt auto-fills again.
  useEffect(() => {
    if (mode !== "register") {
      setSlugManuallyEdited(false);
      return;
    }
    if (slugManuallyEdited) return;
    const derived = slugifyOrgName(orgName);
    if (derived !== orgSlug) setOrgSlug(derived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgName, mode, slugManuallyEdited]);

  // Register-mode: live slug availability via the dedicated
  // `/v1/public/slug-available/:slug` endpoint. The old approach
  // used the public brand endpoint which returns 200-with-nulls
  // for both "no such org" and "org with no branding" — so it
  // couldn't tell "available" from "taken". The new endpoint
  // hits the organisations table directly.
  useEffect(() => {
    if (mode !== "register") {
      setSlugAvailability(null);
      return;
    }
    const slug = orgSlug.trim().toLowerCase();
    if (!slug) {
      setSlugAvailability(null);
      return;
    }
    if (!SLUG_PATTERN.test(slug)) {
      setSlugAvailability("invalid");
      return;
    }
    setSlugAvailability("checking");
    const timer = window.setTimeout(async () => {
      try {
        const res = await api.checkSlugAvailable(slug);
        if (res.available) {
          setSlugAvailability("available");
        } else if (res.reason === "invalid") {
          setSlugAvailability("invalid");
        } else {
          setSlugAvailability("taken");
        }
      } catch {
        setSlugAvailability(null);
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [orgSlug, mode]);

  // Debounced lookup for the "sign in with SSO" button. We only surface it
  // when the IdP declares the email's domain belongs to it.
  useEffect(() => {
    if (!email || !EMAIL_PATTERN.test(email)) {
      setSsoHint(null);
      return;
    }
    const timer = window.setTimeout(() => {
      fetch(
        `${API_BASE_URL}/v1/public/sso/discover?email=${encodeURIComponent(email)}`,
      )
        .then((r) => (r.ok ? r.json() : null))
        .then((body) => {
          if (body && body.found) setSsoHint(body);
          else setSsoHint(null);
        })
        .catch(() => setSsoHint(null));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [email]);

  const emailError = emailTouched && email && !EMAIL_PATTERN.test(email)
    ? "Enter a valid email address."
    : null;
  const slugError = slugTouched && orgSlug && !SLUG_PATTERN.test(orgSlug)
    ? "3–40 lowercase letters, digits or hyphens. Can't start or end with a hyphen."
    : null;
  const pwStrength = passwordStrength(password);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return;
    setError(null);
    setErrorKind(null);
    if (mode === "register" && !termsAccepted) {
      setError("You must accept the Terms and Privacy Policy to create an organisation.");
      setErrorKind("other");
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await api.login(
          {
            email,
            password,
            ...(orgSlug ? { org_slug: orgSlug } : {}),
          },
          turnstileToken,
        );
        setAccessToken(res.access_token);
        const active = res.memberships.find(
          (m) => m.organisation_id === res.active_organisation_id,
        );
        // Multi-tenant with no resolved org: the token is NOT bound to a
        // tenant, so navigating in would 403 every org call. Show the picker;
        // the choice re-scopes the session via switch-org.
        if (!active && res.memberships.length > 1) {
          setUser({
            id: res.user.id,
            email: res.user.email,
            full_name: res.user.full_name,
            active_organisation_id: null,
            needs_verification: res.user.needs_verification,
            verification_grace_until: res.user.verification_grace_until,
          });
          setOrgChoices({ user: res.user, memberships: res.memberships });
          return;
        }
        if (active) setActiveOrgSlug(active.slug);
        else if (res.memberships[0]) setActiveOrgSlug(res.memberships[0].slug);
        setUser({
          id: res.user.id,
          email: res.user.email,
          full_name: res.user.full_name,
          ...(active ? { role: active.role, is_examiner: active.is_examiner } : {}),
          active_organisation_id: res.active_organisation_id,
          needs_verification: res.user.needs_verification,
          verification_grace_until: res.user.verification_grace_until,
        });
        nav(redirectTo, { replace: true });
      } else {
        const res = await api.register(
          {
            email,
            password,
            full_name: fullName,
            org_name: orgName,
            org_slug: orgSlug,
          },
          turnstileToken,
        );
        setAccessToken(res.access_token);
        setActiveOrgSlug(res.organisation_slug);
        // The register response is flat (no nested user / memberships) — build
        // the stored user from the response ids + the form inputs (email/name,
        // already in scope). The founder is always tenant_admin.
        setUser({
          id: res.user_id,
          email,
          full_name: fullName,
          role: "tenant_admin",
          active_organisation_id: res.organisation_id,
          // The founder just signed up — unverified, inside the grace window.
          // The exact deadline is filled in by the VerifyEmailBanner's /auth/me
          // refresh on the next mount; flag it now so the nag shows immediately.
          needs_verification: true,
          verification_grace_until: null,
        });
        // The brand-new org is still 'provisioning' (the tenant D1 is created
        // by an async job that typically completes within a few seconds).
        nav(redirectTo, { replace: true });
      }
    } catch (e) {
      // The submit consumed the single-use Turnstile token; drop it and re-run
      // the widget so a retry has a fresh one (else the next POST 403s).
      setTurnstileToken(null);
      setTurnstileReset((n) => n + 1);
      // Map error shapes to a friendly class so we can both style the
      // banner and point users at the right next step (forgot
      // password / resend confirmation / try again).
      if (e instanceof ApiError) {
        const code = (e as ApiError & { code?: string }).code ?? "";
        const msg = e.message || "";
        if (e.status === 401 || /invalid.*credentials|wrong password/i.test(msg)) {
          setErrorKind("credentials");
          setError(
            mode === "login"
              ? "Email or password is incorrect. Check your details or reset your password."
              : "Could not create the account, please check your details and try again.",
          );
        } else if (e.status === 429 || code === "rate_limited" || /too many/i.test(msg)) {
          setErrorKind("rate_limit");
          setError("Too many attempts. Wait a minute before trying again.");
        } else if (code === "account_suspended" || /suspend/i.test(msg)) {
          setErrorKind("suspended");
          setError("This account is suspended. Contact your administrator.");
        } else if (code === "email_not_confirmed" || /not confirmed|verify/i.test(msg)) {
          setErrorKind("not_confirmed");
          setError(
            "Verify your email to sign in. Open the verification link we emailed when you signed up, it confirms your address and unlocks your workspace.",
          );
        } else if (
          code === "turnstile_failed" ||
          /turnstile|captcha|human.?verification/i.test(msg)
        ) {
          setErrorKind("turnstile");
          setError("The anti-abuse check failed. Refresh the page and try again.");
        } else {
          setErrorKind("other");
          setError(msg || "Sign-in failed");
        }
      } else if (e instanceof Error && e.name === "ZodError") {
        // A response-shape mismatch (what silently broke signup before): surface
        // it honestly instead of a misleading "check your connection" message.
        // Name-check (not instanceof) — zod isn't a direct web dep + avoids
        // cross-package realm issues.
        setErrorKind("other");
        setError(
          "The server returned an unexpected response. Please try again, or contact support if it persists.",
        );
      } else {
        setErrorKind("other");
        setError("Sign-in failed, check your connection and try again.");
      }
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  }

  async function enterOrg(m: LoginResponse["memberships"][number]) {
    if (!orgChoices || enteringSlug) return;
    setEnteringSlug(m.slug);
    setError(null);
    try {
      const res = await api.switchOrg(m.slug);
      setAccessToken(res.access_token);
      setActiveOrgSlug(m.slug);
      setUser({
        id: orgChoices.user.id,
        email: orgChoices.user.email,
        full_name: orgChoices.user.full_name,
        role: m.role,
        is_examiner: m.is_examiner,
        active_organisation_id: res.active_organisation_id,
        needs_verification: orgChoices.user.needs_verification,
        verification_grace_until: orgChoices.user.verification_grace_until,
      });
      nav(redirectTo, { replace: true });
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Could not open that organisation. Please try again.",
      );
      setEnteringSlug(null);
    }
  }

  // Organisation picker — shown when a multi-tenant user has no single active
  // org, so they choose which to open instead of hitting 403s.
  if (orgChoices) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1.5">
          <CardTitle className="text-2xl">Choose your organisation</CardTitle>
          <CardDescription>
            Your account belongs to more than one organisation. Pick the one you
            want to open.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}
          {orgChoices.memberships.map((m) => (
            <button
              key={m.organisation_id}
              type="button"
              onClick={() => void enterOrg(m)}
              disabled={enteringSlug != null}
              className="group flex w-full items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 text-left transition-colors hover:border-primary/50 hover:bg-muted disabled:pointer-events-none disabled:opacity-60"
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">{m.name}</span>
                <span className="block text-xs capitalize text-muted-foreground">
                  {m.role.replace(/_/g, " ")}
                  {m.is_examiner ? " · examiner" : ""}
                </span>
              </span>
              {enteringSlug === m.slug ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              )}
            </button>
          ))}
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            variant="ghost"
            className="text-sm text-muted-foreground"
            disabled={enteringSlug != null}
            onClick={() => {
              clearSession();
              setOrgChoices(null);
              setError(null);
              setPassword("");
            }}
          >
            Sign in as a different user
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card
      className="w-full max-w-md"
      style={
        brand?.primary_hsl
          ? ({ "--tenant-brand": brand.primary_hsl } as React.CSSProperties)
          : undefined
      }
    >
      {brand && (
        <div
          className="flex items-center gap-3 border-b bg-muted/30 px-6 py-3"
          style={
            brand.primary_hsl
              ? { borderTop: `3px solid hsl(${brand.primary_hsl})` }
              : undefined
          }
        >
          {brand.logo_r2_key && (
            <img
              src={`${API_BASE_URL}/v1/public/brand-logo/${brand.slug}`}
              alt=""
              className="h-8 w-8 rounded object-contain"
              loading="lazy"
            />
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Signing into
            </p>
            <p className="truncate text-sm font-medium">{brand.slug}</p>
          </div>
        </div>
      )}
      <CardHeader className="space-y-4">
        <div className="space-y-1.5">
          <CardTitle className="text-2xl">
            {mode === "login"
              ? "Sign in to ExamBase"
              : "Set up your ExamBase workspace"}
          </CardTitle>
          <CardDescription>
            {mode === "login" ? (
              <>
                Author, deliver, and mark Functional Skills assessments
                in one workspace.
              </>
            ) : (
              <>
                A dedicated workspace for your training provider or
                college. Free to set up, you can explore the platform
                before enrolling any learners.
              </>
            )}
          </CardDescription>
        </div>
        <Tabs
          value={mode}
          onValueChange={(v) => setMode(v as "login" | "register")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Sign in</TabsTrigger>
            <TabsTrigger value="register">Create workspace</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      {ssoHint && mode === "login" && (
        <div className="border-t border-border bg-muted/30 p-4">
          <Button
            variant="outline"
            className="w-full"
            type="button"
            onClick={() => {
              window.location.href = `${API_BASE_URL}/v1/auth/sso/${ssoHint.organisation_slug}/authorize`;
            }}
          >
            <ShieldCheck className="mr-2 h-4 w-4" /> Continue with SSO
            {ssoHint.organisation_name ? `, ${ssoHint.organisation_name}` : ""}
          </Button>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Your organisation uses single sign-on for this email domain.
          </p>
        </div>
      )}
      <form onSubmit={onSubmit}>
        <CardContent className="space-y-4">
          {mode === "register" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="full_name">Your full name</Label>
                <Input
                  id="full_name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  autoComplete="name"
                  placeholder="Jane Bennet"
                />
                <p className="text-xs text-muted-foreground">
                  You'll be the first administrator for this workspace.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="org_name">Organisation name</Label>
                <Input
                  id="org_name"
                  type="text"
                  placeholder="St Mary's College"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  The name learners and examiners will see. You can change this
                  later.
                </p>
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              ref={emailInputRef}
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              required
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              aria-invalid={!!emailError}
              aria-describedby={emailError ? "email_err" : undefined}
            />
            {emailError && (
              <p id="email_err" className="text-xs text-destructive">
                {emailError}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              {mode === "login" && (
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  Forgot password?
                </Link>
              )}
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === "register" ? 10 : 1}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                aria-describedby={pwStrength && pwStrength.label ? "pw_hint" : undefined}
                className="pr-10"
              />
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {mode === "register" && (
              <div className="space-y-1" id="pw_hint">
                {/* Visual strength meter, three segments that fill in
                    and colour-change as the password reaches each
                    threshold. A text-only hint (as was here before)
                    is easy to miss; sighted users scan the meter. */}
                <div className="flex gap-1" aria-hidden>
                  {[1, 2, 3].map((seg) => {
                    const active =
                      (pwStrength.tone === "warn" && seg === 1) ||
                      (pwStrength.tone === "ok" && seg <= 2) ||
                      (pwStrength.tone === "strong" && seg <= 3);
                    const colour =
                      pwStrength.tone === "strong"
                        ? "bg-success"
                        : pwStrength.tone === "ok"
                        ? "bg-success/70"
                        : pwStrength.tone === "warn"
                        ? "bg-warning"
                        : "bg-muted";
                    return (
                      <span
                        key={seg}
                        className={cn(
                          "h-1 flex-1 rounded-full",
                          active ? colour : "bg-muted",
                        )}
                      />
                    );
                  })}
                </div>
                <p
                  className={cn(
                    "text-xs",
                    pwStrength.tone === "warn" && "text-warning-text",
                    pwStrength.tone === "ok" && "text-success-text",
                    pwStrength.tone === "strong" && "text-success-text font-medium",
                    pwStrength.tone === "none" && "text-muted-foreground",
                  )}
                >
                  {pwStrength.label || "At least 10 characters. 14+ is stronger."}
                </p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="org_slug">
              {mode === "register" ? "Workspace URL" : "Organisation slug"}
              {mode === "login" && (
                <span className="ml-1 font-normal text-muted-foreground">
                  (optional, only if you belong to several)
                </span>
              )}
            </Label>
            {mode === "register" && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <span className="rounded border bg-muted/50 px-1.5 py-0.5 font-mono">
                  exambase.uk/
                </span>
                <span className="font-mono">{orgSlug || "your-slug"}</span>
              </div>
            )}
            <Input
              id="org_slug"
              type="text"
              value={orgSlug}
              onChange={(e) => {
                setOrgSlug(e.target.value.toLowerCase());
                if (mode === "register") setSlugManuallyEdited(true);
              }}
              onBlur={() => setSlugTouched(true)}
              required={mode === "register"}
              pattern="[a-z0-9][a-z0-9\-]{1,38}[a-z0-9]"
              placeholder={mode === "register" ? "st-marys-college" : "my-college"}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={!!slugError}
              aria-describedby={slugError ? "slug_err" : "slug_hint"}
            />
            {slugError ? (
              <p id="slug_err" className="text-xs text-destructive">
                {slugError}
              </p>
            ) : mode === "register" && slugAvailability ? (
              <p
                id="slug_hint"
                className={cn(
                  "text-xs",
                  slugAvailability === "available" && "text-success-text",
                  slugAvailability === "taken" && "text-destructive",
                  slugAvailability === "invalid" && "text-destructive",
                  (slugAvailability === "checking") && "text-muted-foreground",
                )}
              >
                {slugAvailability === "checking" && "Checking availability…"}
                {slugAvailability === "available" &&
                  `exambase.uk/${orgSlug} is available.`}
                {slugAvailability === "taken" &&
                  "That slug is already taken. Try another."}
                {slugAvailability === "invalid" &&
                  "Lowercase letters, digits, hyphens. 3–40 chars."}
              </p>
            ) : (
              mode === "register" && (
                <p id="slug_hint" className="text-xs text-muted-foreground">
                  Lowercase letters, digits, hyphens. 3–40 chars. Your workspace URL.
                </p>
              )
            )}
          </div>
          {mode === "register" && (
            <label className="flex cursor-pointer items-start gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5"
                aria-required
              />
              <span>
                I agree to the{" "}
                <Link
                  to="/terms"
                  className="underline hover:text-foreground"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </Link>
                {" "}and{" "}
                <Link
                  to="/privacy"
                  className="underline hover:text-foreground"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
          )}
          <Turnstile
            action={mode === "login" ? "login" : "register"}
            onToken={onTurnstileToken}
            resetSignal={turnstileReset}
          />
          {error && (
            <div
              role="alert"
              className="space-y-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              <p>{error}</p>
              {errorKind === "credentials" && mode === "login" && (
                <div className="flex gap-3 text-xs">
                  <Link to="/forgot-password" className="underline">
                    Reset password
                  </Link>
                </div>
              )}
              {errorKind === "not_confirmed" && (
                <p className="text-xs opacity-90">
                  The link expires 24 hours after signup. If yours has expired,
                  contact your administrator to re-send it.
                </p>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button
            type="submit"
            disabled={busy || turnstileMissing}
            className="w-full"
          >
            {(busy || turnstileChecking) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {busy
              ? mode === "login"
                ? "Signing in…"
                : "Creating…"
              : turnstileChecking
                ? "Checking your browser…"
                : turnstileMissing
                  ? "Complete the check above"
                  : mode === "login"
                    ? "Sign in"
                    : "Create workspace"}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
